/**
 * Sound for the game (Phase 5D). Every effect is synthesised with Web Audio at
 * play time, from oscillators and one shared noise buffer, so no third-party
 * audio is bundled. Buses: master → { effects, music }.
 *
 * Browser rules: an AudioContext may only start after a user gesture, so
 * nothing is created until `unlock()` is called from a click or key press.
 * The context is suspended while the page is hidden and resumed when it
 * returns. When Web Audio is missing or fails, every call is a no-op.
 *
 * Repetition: each effect has a minimum interval, and repeats within a short
 * window are quieter and slightly detuned, so rapid hopping does not turn
 * into a wall of identical clicks.
 *
 * Music: the bus and `playMusic(url)` exist, but no music ships with this
 * build (there is no suitably licensed track yet), so the game plays none.
 */

export type EffectName = 'ui' | 'uiBack' | 'jump' | 'land' | 'impact' | 'death' | 'checkpoint' | 'finish' | 'reactive' | 'invalid';

interface EffectSpec {
  /** Minimum seconds between two plays. */
  minInterval: number;
  /** Repeats within this window are attenuated and detuned. */
  repeatWindow: number;
}

const SPECS: Readonly<Record<EffectName, EffectSpec>> = {
  ui: { minInterval: 0.03, repeatWindow: 0.2 },
  uiBack: { minInterval: 0.03, repeatWindow: 0.2 },
  jump: { minInterval: 0.06, repeatWindow: 0.45 },
  land: { minInterval: 0.08, repeatWindow: 0.45 },
  impact: { minInterval: 0.12, repeatWindow: 0.5 },
  death: { minInterval: 0.3, repeatWindow: 1 },
  checkpoint: { minInterval: 0.2, repeatWindow: 0.5 },
  finish: { minInterval: 0.5, repeatWindow: 1 },
  reactive: { minInterval: 0.25, repeatWindow: 0.8 },
  invalid: { minInterval: 0.3, repeatWindow: 1 },
};

export interface Volumes {
  master: number;
  music: number;
  effects: number;
}

type AudioContextCtor = new () => AudioContext;

/** Gain multiplier for a repeat `n` plays into its repeat window (1, 0.7, 0.55, … ≥ 0.35). */
export function repeatGain(repeats: number): number {
  return Math.max(0.35, 1 / (1 + 0.43 * repeats));
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private effects: GainNode | null = null;
  private music: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private wind: { gain: GainNode; filter: BiquadFilterNode; source: AudioBufferSourceNode } | null = null;
  private musicSource: { source: AudioBufferSourceNode; gain: GainNode } | null = null;
  private volumes: Volumes = { master: 0.8, music: 0.6, effects: 0.8 };
  private readonly lastPlay = new Map<EffectName, { at: number; repeats: number }>();
  private readonly abort = new AbortController();
  private failed = false;
  /** Count of effects actually started (tests and diagnostics). */
  played = 0;

  constructor(private readonly ctor: AudioContextCtor | null = defaultCtor()) {
    globalThis.document?.addEventListener(
      'visibilitychange',
      () => {
        if (!this.ctx) return;
        if (document.visibilityState === 'hidden') void this.ctx.suspend().catch(() => undefined);
        else void this.ctx.resume().catch(() => undefined);
      },
      { signal: this.abort.signal },
    );
  }

  /** 'off' before the first gesture, 'unavailable' without Web Audio. */
  get state(): 'off' | 'unavailable' | AudioContextState {
    if (this.failed || !this.ctor) return 'unavailable';
    return this.ctx?.state ?? 'off';
  }

  /** Creates or resumes the context. Call from a user gesture. */
  unlock(): void {
    if (this.failed || !this.ctor) return;
    try {
      if (!this.ctx) this.build(new this.ctor());
      if (this.ctx!.state === 'suspended' && globalThis.document?.visibilityState !== 'hidden') void this.ctx!.resume().catch(() => undefined);
    } catch (err: unknown) {
      this.failed = true;
      console.warn('Audio is unavailable; the game continues without sound.', err);
    }
  }

  setVolumes(v: Volumes): void {
    this.volumes = { ...v };
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.master!.gain.setTargetAtTime(v.master, t, 0.02);
    this.effects!.gain.setTargetAtTime(v.effects, t, 0.02);
    this.music!.gain.setTargetAtTime(v.music, t, 0.02);
  }

  /** Plays one effect. `intensity` 0..1 scales loudness (and brightness where it applies). */
  play(name: EffectName, intensity = 1): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running' || this.volumes.master <= 0 || this.volumes.effects <= 0) return;
    const now = ctx.currentTime;
    const spec = SPECS[name];
    const prev = this.lastPlay.get(name);
    if (prev && now - prev.at < spec.minInterval) return;
    const repeats = prev && now - prev.at < spec.repeatWindow ? prev.repeats + 1 : 0;
    this.lastPlay.set(name, { at: now, repeats });
    const gain = repeatGain(repeats) * Math.max(0.05, Math.min(1, intensity));
    const detune = repeats ? (Math.random() * 2 - 1) * 60 : 0;
    this.synth(name, now, gain, detune, intensity);
    this.played++;
  }

  /** Rushing air: level 0..1 (from speed and nearby geometry), brightness 0..1. */
  setWind(level: number, brightness: number): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    if (!this.wind && level > 0.01) this.startWind();
    if (!this.wind) return;
    const t = ctx.currentTime;
    this.wind.gain.gain.setTargetAtTime(Math.max(0, Math.min(1, level)) * 0.22, t, 0.12);
    this.wind.filter.frequency.setTargetAtTime(350 + brightness * 1500, t, 0.15);
  }

  /** Loops a music file on the music bus (fades in). No music ships with this build. */
  async playMusic(url: string): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const data = await (await fetch(url)).arrayBuffer();
    const buffer = await ctx.decodeAudioData(data);
    this.stopMusic();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(1, ctx.currentTime + 2);
    source.connect(gain).connect(this.music!);
    source.start();
    this.musicSource = { source, gain };
  }

  stopMusic(): void {
    const m = this.musicSource;
    if (!m || !this.ctx) return;
    const t = this.ctx.currentTime;
    m.gain.gain.setTargetAtTime(0, t, 0.3);
    m.source.stop(t + 1.5);
    this.musicSource = null;
  }

  /** Silences continuous sound (wind) when gameplay stops. */
  quiet(): void {
    if (this.wind && this.ctx) this.wind.gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
  }

  dispose(): void {
    this.abort.abort();
    try {
      this.wind?.source.stop();
    } catch {
      // Already stopped.
    }
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    this.wind = null;
  }

  // ---------------------------------------------------------------- synthesis

  private build(ctx: AudioContext): void {
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.effects = ctx.createGain();
    this.music = ctx.createGain();
    // A gentle limiter so overlapping effects never clip.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -10;
    limiter.ratio.value = 8;
    this.effects.connect(this.master);
    this.music.connect(this.master);
    this.master.connect(limiter).connect(ctx.destination);
    const n = ctx.sampleRate;
    this.noise = ctx.createBuffer(1, n, n);
    const ch = this.noise.getChannelData(0);
    let seed = 0x2545f491;
    for (let i = 0; i < n; i++) {
      seed ^= seed << 13;
      seed ^= seed >>> 17;
      seed ^= seed << 5;
      ch[i] = ((seed >>> 0) / 0xffffffff) * 2 - 1;
    }
    this.setVolumes(this.volumes);
  }

  private startWind(): void {
    const ctx = this.ctx!;
    const source = ctx.createBufferSource();
    source.buffer = this.noise;
    source.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 400;
    filter.Q.value = 0.7;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    source.connect(filter).connect(gain).connect(this.effects!);
    source.start();
    this.wind = { gain, filter, source };
  }

  private tone(type: OscillatorType, from: number, to: number, start: number, dur: number, peak: number, detune: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.detune.value = detune;
    osc.frequency.setValueAtTime(from, start);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), start + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), start + Math.min(0.012, dur / 4));
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(g).connect(this.effects!);
    osc.start(start);
    osc.stop(start + dur + 0.02);
  }

  private hiss(start: number, dur: number, peak: number, type: BiquadFilterType, freq: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(Math.max(0.0002, peak), start);
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    src.connect(f).connect(g).connect(this.effects!);
    src.start(start, Math.random() * 0.5);
    src.stop(start + dur + 0.02);
  }

  private synth(name: EffectName, t: number, gain: number, detune: number, intensity: number): void {
    switch (name) {
      case 'ui':
        this.tone('sine', 880, 990, t, 0.06, 0.12 * gain, detune);
        break;
      case 'uiBack':
        this.tone('sine', 660, 520, t, 0.07, 0.11 * gain, detune);
        break;
      case 'jump':
        this.tone('triangle', 240, 420, t, 0.09, 0.09 * gain, detune);
        this.hiss(t, 0.07, 0.04 * gain, 'highpass', 2500);
        break;
      case 'land':
        this.tone('sine', 140, 55, t, 0.12, 0.22 * gain, detune);
        this.hiss(t, 0.08, 0.06 * gain, 'lowpass', 500 + 900 * intensity);
        break;
      case 'impact':
        this.tone('sine', 110, 40, t, 0.22, 0.32 * gain, detune);
        this.hiss(t, 0.18, 0.12 * gain, 'lowpass', 1400);
        break;
      case 'death':
        this.tone('sawtooth', 320, 70, t, 0.45, 0.12 * gain, detune);
        this.hiss(t, 0.35, 0.1 * gain, 'lowpass', 700);
        break;
      case 'checkpoint':
        this.tone('triangle', 659, 659, t, 0.25, 0.12 * gain, detune);
        this.tone('triangle', 988, 988, t + 0.08, 0.32, 0.1 * gain, detune);
        break;
      case 'finish':
        [523, 659, 784, 1047].forEach((f, i) => this.tone('triangle', f, f, t + i * 0.09, 0.5, 0.11 * gain, detune));
        break;
      case 'reactive':
        this.tone('sine', 1320, 1760, t, 0.18, 0.05 * gain, detune);
        this.tone('sine', 1980, 2200, t + 0.03, 0.16, 0.03 * gain, detune);
        break;
      case 'invalid':
        this.tone('square', 220, 180, t, 0.16, 0.05 * gain, detune);
        break;
    }
  }
}

function defaultCtor(): AudioContextCtor | null {
  const w = globalThis as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}
